import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import type {
  FeatureAccountDto,
  SelfOnboardUpdatePayload,
  UserBankFeature,
  userBankList,
} from "@/app/types/adminMenu";

interface ManageUserSliceState {
  finalPayload: {
    userData: SelfOnboardUpdatePayload;
    userFeatureDto: UserBankFeature[];
    UserBankList: userBankList[];
  } | null;
}

const initialState: ManageUserSliceState = {
  finalPayload: null,
};

const manageUserSlice = createSlice({
  name: "manageUserSlice",
  initialState,
  reducers: {
    setUser: (state, action: PayloadAction<SelfOnboardUpdatePayload>) => {
      state.finalPayload = {
        userData: action.payload,
        userFeatureDto: action.payload.userFeatureDto ?? [],
        UserBankList: action.payload.UserBankList ?? [],
      };
    },

    updateFeatureAccountDto: (
      state,
      action: PayloadAction<{
        subFeatureId: number;
        featureAccountDto: FeatureAccountDto[];
      }>
    ) => {
      if (!state.finalPayload) return;
      const { subFeatureId, featureAccountDto } = action.payload;
      state.finalPayload.userFeatureDto = state.finalPayload.userFeatureDto.map(
        (feature) =>
          feature.subFeatureId === subFeatureId
            ? {
                ...feature,
                featureAccountDto,
                checkBox: featureAccountDto.some((acc) => acc.checkBox === "Y")
                  ? "Y"
                  : "N",
              }
            : feature
      );
    },

    setFinalPayload: (
      state,
      action: PayloadAction<ManageUserSliceState["finalPayload"]>
    ) => {
      state.finalPayload = action.payload;
    },

    upsertBankInList: (state, action: PayloadAction<userBankList>) => {
      if (!state.finalPayload) return;

      if (!state.finalPayload.UserBankList) {
        state.finalPayload.UserBankList = [];
      }

      const incoming = action.payload;
      const bankIdx = state.finalPayload.UserBankList.findIndex(
        (b) => b.bankId === incoming.bankId
      );

      if (bankIdx >= 0) {
        const existingFeatureList =
          state.finalPayload.UserBankList[bankIdx].userBankFeatureList;

        state.finalPayload.UserBankList[bankIdx] = {
          ...incoming,
          userBankFeatureList:
            existingFeatureList && existingFeatureList.length > 0
              ? existingFeatureList
              : incoming.userBankFeatureList ?? [],
        };
      } else {
        state.finalPayload.UserBankList.push({
          ...incoming,
          userBankFeatureList: incoming.userBankFeatureList ?? [],
        });
      }
    },

    // ✅ Added — was missing entirely from this slice
    upsertFeatureInBankList: (
      state,
      action: PayloadAction<{ selectedBankId: number; feature: UserBankFeature[];
 }>
    ) => {
      const { selectedBankId, feature } = action.payload;

      if (!state.finalPayload) return;

      if (!state.finalPayload.UserBankList) {
        state.finalPayload.UserBankList = [];
      }

      // Find the bank entry
      const bankIdx = state.finalPayload.UserBankList.findIndex(
        (b) => b.bankId === selectedBankId
      );

      if (bankIdx >= 0) {
        // Normalize null → []
        if (!state.finalPayload.UserBankList[bankIdx].userBankFeatureList) {
          state.finalPayload.UserBankList[bankIdx].userBankFeatureList = [];
        }

        const featureList =
          state.finalPayload.UserBankList[bankIdx].userBankFeatureList!;
        const featureIdx = featureList.findIndex(
          (f) => f.subFeatureId === feature.subFeatureId
        );

        if (featureIdx >= 0) {
          featureList[featureIdx] = feature;
        } else {
          featureList.push(feature);
        }
      } else {
        // Bank not yet added — safety fallback, upsertBankInList should run first
        state.finalPayload.UserBankList.push({
          bankId: selectedBankId,
          userBankFeatureList: [feature],
        } as any);
      }
    },

    clearFinalPayload: (state) => {
      state.finalPayload = null;
    },
  },
});

export const {
  setUser,
  setFinalPayload,
  clearFinalPayload,
  updateFeatureAccountDto,
  upsertBankInList,
  upsertFeatureInBankList,  // ✅ now exported
} = manageUserSlice.actions;

export default manageUserSlice.reducer;